/**
 * TableManager.js
 * 
 * Complete table and seating management for tournaments including:
 * - Pre-define tables with configurable seat counts
 * - Drag-and-drop for unseated AND seated players
 * - Auto-seat functionality for quick setup
 * - Player elimination from seats
 * - Rebuy and addon management
 * - Grid and list view modes
 */

import React, { useState, useEffect, useCallback } from 'react';

const TableManager = ({ tournamentId }) => {
  // State management
  const [tournament, setTournament] = useState(null);
  const [tables, setTables] = useState([]);
  const [tableConfigs, setTableConfigs] = useState([]);
  const [unseatedPlayers, setUnseatedPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  
  // Table setup states
  const [showTableSetup, setShowTableSetup] = useState(false);
  const [newTableNumber, setNewTableNumber] = useState('');
  const [newSeatCount, setNewSeatCount] = useState('8');
  
  // Drag and drop states
  const [draggedPlayer, setDraggedPlayer] = useState(null);
  const [draggedFromSeat, setDraggedFromSeat] = useState(null); // Track if dragging from a seat
  const [dragOverSeat, setDragOverSeat] = useState(null);

  // Tap-to-move state (for touch devices)
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedFromSeat, setSelectedFromSeat] = useState(null);
  const [collapsedTables, setCollapsedTables] = useState(new Set());
  
  // Elimination modal state
  const [showEliminationModal, setShowEliminationModal] = useState(false);
  const [eliminatingPlayer, setEliminatingPlayer] = useState(null);
  const [eliminatedByPlayer, setEliminatedByPlayer] = useState('');
  const [position, setPosition] = useState('');
  const [prizeAmount, setPrizeAmount] = useState('');
  const [balanceSuggestion, setBalanceSuggestion] = useState(null);

  const [redrawProposal, setRedrawProposal] = useState(null);
  const [lockMode, setLockMode] = useState(false);
  const [lockedSeats, setLockedSeats] = useState([]);

  /**
   * Get API URL based on hostname
   */
  const getApiUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3000';
    } else if (hostname === '10.0.0.28') {
      return `https://${hostname}/api`;
    } else {
      return `https://${hostname}/api`;
    }
  };

  /**
   * Load table configurations
   */
  const loadTableConfigs = useCallback(async () => {
    try {
      const configRes = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/table-config`);
      const configData = await configRes.json();
      setTableConfigs(configData);
    } catch (error) {
      console.error('Error loading table configs:', error);
    }
  }, [tournamentId]);

  /**
   * Load table data and seating arrangements
   */
  const loadTableData = useCallback(async () => {
    try {
      // Load tournament info
      const tournamentRes = await fetch(`${getApiUrl()}/tournaments/${tournamentId}`);
      const tournamentData = await tournamentRes.json();
      setTournament(tournamentData);

      // Load seating chart
      const seatingRes = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/seating`);
      const seatingData = await seatingRes.json();
      
      // Organize by tables
      const tableMap = {};
      seatingData.forEach(seat => {
        if (!tableMap[seat.table_number]) {
          tableMap[seat.table_number] = {
            tableNumber: seat.table_number,
            seats: []
          };
        }
        tableMap[seat.table_number].seats.push(seat);
      });
      
      // Sort seats within each table
      Object.values(tableMap).forEach(table => {
        table.seats.sort((a, b) => a.seat_number - b.seat_number);
      });
      
      setTables(Object.values(tableMap).sort((a, b) => a.tableNumber - b.tableNumber));

      // Load unseated active players
      const registrationsRes = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/registrations`);
      const registrations = await registrationsRes.json();
      
      const unseated = registrations.filter(reg => 
        reg.status === 'active' && !seatingData.some(seat => seat.registration_id === reg.id)
      );
      setUnseatedPlayers(unseated);

      setLoading(false);
    } catch (error) {
      console.error('Error loading table data:', error);
      setLoading(false);
    }
  }, [tournamentId]);

  const breakTableAndReseat = async (tableNumber) => {
    if (!window.confirm(
      `Break Table ${tableNumber} and redistribute its players to balanced open seats?\n\n` +
      `Players will be moved to the emptiest tables. This cannot be undone.`
    )) return;

    try {
      const response = await fetch(
        `${getApiUrl()}/tournaments/${tournamentId}/break-table-reseat/${tableNumber}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );

      const result = await response.json();

      if (response.ok) {
        // Show where each player was moved
        if (result.reseated && result.reseated.length > 0) {
          const moves = result.reseated
            .map(r => `• ${r.player} → Table ${r.to_table}, Seat ${r.to_seat}`)
            .join('\n');
          alert(`Table ${tableNumber} broken.\n\nPlayers reseated:\n\n${moves}`);
        } else {
          alert(result.message || `Table ${tableNumber} broken.`);
        }
        loadTableData();
      } else {
        alert(`Error: ${result.error || 'Failed to break table'}`);
      }
    } catch (error) {
      console.error('Error breaking table:', error);
      alert('Failed to break table');
    }
  };

  const proposeRedraw = async () => {
    const res = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/redraw-proposal`);
    const data = await res.json();
    if (data.error) { alert(data.error); return; }
    setRedrawProposal(data);
  };

 const loadLockedSeats = async () => {
    try {
      const res = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/locked-seats`);
      if (res.ok) setLockedSeats(await res.json());
    } catch (e) { console.error('Error loading locked seats:', e); }
  };

  const isSeatLocked = (tableNum, seatNum) =>
    lockedSeats.some(l => l.table_number === tableNum && l.seat_number === seatNum);

  const toggleSeatLock = async (tableNum, seatNum) => {
    const currentlyLocked = isSeatLocked(tableNum, seatNum);
    try {
      const res = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/lock-seat`, {
        method: currentlyLocked ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
        body: JSON.stringify({ table_number: tableNum, seat_number: seatNum })
      });
      if (res.ok) {
        loadLockedSeats();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to toggle seat lock');
      }
    } catch (e) { alert('Failed to toggle seat lock'); }
  }; 

const executeRedraw = async () => {
    if (!window.confirm('Apply this redraw? All players will be reseated.')) return;
    const proposalToPrint = redrawProposal; // capture before clearing
    const res = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/redraw-execute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify({ tables: redrawProposal.tables })
    });
    if (res.ok) {
      setRedrawProposal(null);
      loadTableData();
      if (window.confirm('Redraw complete. Print table sheets now?')) {
        printRedrawSheets(proposalToPrint, tournament?.event_name);
      }
    } else {
      alert('Redraw failed');
    }
  };

  const printRedrawSheets = (proposal, eventTitle) => {
    const tables = proposal.tables;
    let pagesHtml = '';
    for (let i = 0; i < tables.length; i += 2) {   // 2 per page now
      const pageTables = tables.slice(i, i + 2);
      const tablesHtml = pageTables.map(table => {
        const sortedPlayers = [...table.players].sort((a, b) => a.seat_number - b.seat_number);
        const rows = sortedPlayers.map(p => `
          <tr>
            <td class="seat">${p.seat_number}</td>
            <td class="name">${p.name}</td>
            <td class="chips">${p.chips.toLocaleString()}</td>
          </tr>
        `).join('');
        return `
          <div class="table-block">
            <div class="table-header">
              Table ${table.table_number}
              <span class="table-meta">${table.player_count} players &middot; ${table.total_chips.toLocaleString()} chips</span>
            </div>
            <table class="seat-table">
              <thead>
                <tr><th class="seat">Seat</th><th class="name">Player</th><th class="chips">Chips</th></tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
          </div>
        `;
      }).join('');

      pagesHtml += `
        <div class="page">
          <div class="sheet-header">
            <img src="/receipt-logo.png" class="logo" alt="" />
            <div class="titles">
              <div class="event-title">${eventTitle || 'Tournament'}</div>
              <div class="subtitle">Table Redraw &middot; ${new Date().toLocaleString()}</div>
            </div>
          </div>
          ${tablesHtml}
        </div>
      `;
    }

    const html = `
      <html>
      <head>
        <style>
          @page { size: A4; margin: 12mm; }
          body { font-family: Arial, sans-serif; margin: 0; }
          .page { page-break-after: always; }
          .page:last-child { page-break-after: auto; }
          .sheet-header {
            display: flex; align-items: center; gap: 15px;
            border-bottom: 3px solid #222; padding-bottom: 10px; margin-bottom: 20px;
          }
          .sheet-header .logo { height: 60px; width: auto; }
          .sheet-header .event-title { font-size: 24px; font-weight: bold; }
          .sheet-header .subtitle { font-size: 13px; color: #555; margin-top: 2px; }
          .table-block { margin-bottom: 24px; border: 2px solid #000; border-radius: 6px; overflow: hidden; }
          .table-header {
            background: #222; color: #fff; padding: 10px 14px;
            font-size: 20px; font-weight: bold;
            display: flex; justify-content: space-between; align-items: center;
          }
          .table-meta { font-size: 14px; font-weight: normal; }
          .seat-table { width: 100%; border-collapse: collapse; }
          .seat-table th, .seat-table td { padding: 8px 14px; border-bottom: 1px solid #ccc; text-align: left; }
          .seat-table th { background: #f0f0f0; font-size: 14px; }
          .seat-table .seat { width: 60px; text-align: center; font-weight: bold; font-size: 16px; }
          .seat-table .chips { text-align: right; font-variant-numeric: tabular-nums; }
          .seat-table td.name { font-size: 16px; }
        </style>
      </head>
      <body>${pagesHtml}</body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(html);
    iframe.contentDocument.close();
    iframe.contentWindow.focus();
    // Wait for the logo image to load before printing
    const logoImg = iframe.contentDocument.querySelector('.logo');
    const doPrint = () => {
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };
    if (logoImg && !logoImg.complete) {
      logoImg.onload = doPrint;
      logoImg.onerror = doPrint; // print even if logo fails
      setTimeout(doPrint, 2000); // fallback timeout
    } else {
      setTimeout(doPrint, 300);
    }
  };

  /**
   * Load all data on mount and set up refresh
   */
useEffect(() => {
    loadTableData();
    loadTableConfigs();
    loadLockedSeats();
    const interval = setInterval(() => {
      loadTableData();
      loadTableConfigs();
      loadLockedSeats();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadTableData, loadTableConfigs]);

  /**
   * Auto-seat all unseated players
   */
  const autoSeatPlayers = async () => {
    if (unseatedPlayers.length === 0) {
      alert('No unseated players to seat');
      return;
    }

    if (!window.confirm(`Auto-seat ${unseatedPlayers.length} player${unseatedPlayers.length > 1 ? 's' : ''}?`)) {
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/auto-seat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        loadTableData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error auto-seating:', error);
      alert('Failed to auto-seat players');
    }
  };

  /**
   * Add a new table configuration
   */
  const addTable = async () => {
    if (!newTableNumber || !newSeatCount) {
      alert('Please enter table number and seat count');
      return;
    }

    const tableNum = parseInt(newTableNumber);
    const seatCnt = parseInt(newSeatCount);

    if (tableNum < 1 || tableNum > 99) {
      alert('Table number must be between 1 and 99');
      return;
    }

    if (seatCnt < 2 || seatCnt > 10) {
      alert('Seat count must be between 2 and 10');
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/table-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          table_number: tableNum,
          seat_count: seatCnt
        })
      });

      if (response.ok) {
        alert(`Table ${tableNum} added with ${seatCnt} seats!`);
        setNewTableNumber('');
        setNewSeatCount('8');
        loadTableConfigs();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error adding table:', error);
      alert('Failed to add table');
    }
  };

  /**
   * Delete a table configuration
   */
  const deleteTable = async (tableId, tableNumber) => {
    if (!window.confirm(`Delete Table ${tableNumber}?`)) {
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/table-config/${tableId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Table deleted successfully!');
        loadTableConfigs();
        loadTableData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting table:', error);
      alert('Failed to delete table');
    }
  };

/**
   * Shared logic: place a player (from unseated list or another seat) into a target seat.
   * Used by BOTH drag-drop and tap-to-move.
   */
  const placePlayerInSeat = async (player, fromSeat, tableNumber, seatNumber, isOccupied) => {
    if (!player) return;

      if (isSeatLocked(tableNumber, seatNumber)) {
      alert('That seat is locked and cannot be used.');
      return;
    }
    if (isOccupied) {
      alert('This seat is already occupied');
      return;
    }

    try {
      if (fromSeat) {
        // Moving a player who is already seated
        const response = await fetch(`${getApiUrl()}/seat-assignments/${fromSeat.seatAssignmentId}/move`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            new_table_number: tableNumber,
            new_seat_number: seatNumber
          })
        });
        if (response.ok) {
          loadTableData();
        } else {
          const error = await response.json();
          alert(`Error: ${error.error}`);
        }
      } else {
        // Assigning an unseated player
        const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/assign-seat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            registration_id: player.id,
            table_number: tableNumber,
            seat_number: seatNumber
          })
        });
        if (response.ok) {
          loadTableData();
        } else {
          const error = await response.json();
          alert(`Error: ${error.error}`);
        }
      }
    } catch (error) {
      console.error('Error placing player:', error);
      alert('Failed to move player');
    }
  };

  // Toggle to Collapse View of Table
  const toggleTableCollapse = (tableNumber) => {
    setCollapsedTables(prev => {
      const next = new Set(prev);
      if (next.has(tableNumber)) {
        next.delete(tableNumber);
      } else {
        next.add(tableNumber);
      }
      return next;
    });
  };

  /**
   * Handle drag start - player is being dragged (unseated or seated)
   */
  const handleDragStart = (e, player, fromSeat = null) => {
    setDraggedPlayer(player);
    setDraggedFromSeat(fromSeat);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
    
    // Add visual feedback
    e.target.style.opacity = '0.4';
  };

  /**
   * Handle drag end - reset styles
   */
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedPlayer(null);
    setDraggedFromSeat(null);
    setDragOverSeat(null);
  };

  /**
   * Handle drag over - allow drop
   */
  const handleDragOver = (e, tableNumber, seatNumber) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSeat(`${tableNumber}-${seatNumber}`);
    return false;
  };

  /**
   * Handle drag leave - remove hover effect
   */
  const handleDragLeave = (e) => {
    setDragOverSeat(null);
  };

  /**
   * Handle drop - assign or move player to seat
   */
  const handleDrop = async (e, tableNumber, seatNumber, isOccupied) => {
    e.stopPropagation();
    e.preventDefault();
    setDragOverSeat(null);

    await placePlayerInSeat(draggedPlayer, draggedFromSeat, tableNumber, seatNumber, isOccupied);

    setDraggedPlayer(null);
    setDraggedFromSeat(null);
  };

/**
   * Tap a player to select them (works on touch and desktop click)
   */
  const handlePlayerTap = (player, fromSeat = null) => {
    // Tapping the already-selected player deselects
    if (selectedPlayer && selectedPlayer.id === player.id && !fromSeat === !selectedFromSeat) {
      setSelectedPlayer(null);
      setSelectedFromSeat(null);
      return;
    }
    setSelectedPlayer(player);
    setSelectedFromSeat(fromSeat);
  };

  /**
   * Tap an empty seat to place the selected player there
   */
  const handleSeatTap = async (tableNumber, seatNumber, isOccupied) => {
    if (!selectedPlayer) return; // nothing selected, ignore
    if (isOccupied) {
      alert('This seat is already occupied');
      return;
    }
    await placePlayerInSeat(selectedPlayer, selectedFromSeat, tableNumber, seatNumber, isOccupied);
    setSelectedPlayer(null);
    setSelectedFromSeat(null);
  };

  /**
   * Unseat a player (remove from seat without eliminating)
   */
  const unseatPlayer = async (seatAssignmentId, playerName, tableNumber, seatNumber) => {
    if (!window.confirm(`Unseat ${playerName} from Table ${tableNumber}, Seat ${seatNumber}?`)) {
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/seat-assignments/${seatAssignmentId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadTableData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error unseating player:', error);
      alert('Failed to unseat player');
    }
  };

  /**
   * Start elimination process for a player
   */
  const startElimination = (player) => {
    setEliminatingPlayer(player);
    setShowEliminationModal(true);
    
    // Calculate next finishing position
    const activePlayers = tables.flatMap(t => t.seats).filter(s => s.player_status === 'active').length;
    setPosition(activePlayers.toString());
  };

  /**
   * Confirm player elimination
   */
  const confirmElimination = async () => {
    if (!eliminatingPlayer) return;

    try {
      const response = await fetch(`${getApiUrl()}/registrations/${eliminatingPlayer.registration_id}/eliminate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eliminated_by_player_id: eliminatedByPlayer || null,
          position_finished: parseInt(position),
          prize_amount: parseFloat(prizeAmount) || 0
        })
      });

      if (response.ok) {
        alert(`${eliminatingPlayer.first_name} ${eliminatingPlayer.last_name} eliminated in position ${position}`);
        setShowEliminationModal(false);
        setEliminatingPlayer(null);
        setEliminatedByPlayer('');
        setPosition('');
        setPrizeAmount('');
        loadTableData();
        checkBalance();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error eliminating player:', error);
      alert('Failed to eliminate player');
    }
  };

  const checkBalance = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/balance-suggestion`);
      const data = await response.json();
      setBalanceSuggestion(data);
    } catch (error) {
      console.error('Error checking balance:', error);
    }
  };

  /**
   * Add rebuy for a player
   */
  const addRebuy = async (playerId, playerName) => {
    if (!window.confirm(`Add rebuy for ${playerName}?`)) return;

    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/rebuy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          player_id: playerId,
          chips_amount: tournament?.rebuy_chips || tournament?.starting_chips || 10000
        })
      });

      if (response.ok) {
        loadTableData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error adding rebuy:', error);
      alert('Failed to add rebuy');
    }
  };

  /**
   * Add addon for a player
   */
  const addAddon = async (playerId, playerName) => {
    if (!window.confirm(`Add addon for ${playerName}?`)) return;

    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/addon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          player_id: playerId,
          chips_amount: tournament?.addon_chips || tournament?.starting_chips || 10000
        })
      });

      if (response.ok) {
        loadTableData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error adding addon:', error);
      alert('Failed to add addon');
    }
  };

  if (loading) {
    return <div className="container">Loading table management...</div>;
  }

  return (
    <div className="container" style={{ maxWidth: '1400px' }}>
      <h1>Table Management - {tournament?.event_name}</h1>

      {/* Controls */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button className="btn btn-success" onClick={() => setShowTableSetup(!showTableSetup)}>
              {showTableSetup ? '❌ Close Setup' : '⚙️ Table Setup'}
            </button>
            <button className="btn" onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}>
              {viewMode === 'grid' ? '📋 List View' : '🎲 Grid View'}
            </button>
            <button className="btn" onClick={() => { loadTableData(); loadTableConfigs(); }}>
              🔄 Refresh
            </button>
            {unseatedPlayers.length > 0 && (
              <button className="btn btn-success" onClick={autoSeatPlayers}>
                🪑 Auto-Seat All ({unseatedPlayers.length})
              </button>
            )}
            <button className="btn" onClick={() => setCollapsedTables(new Set(tableConfigs.map(c => c.table_number)))}>
              ➖ Collapse All
            </button>
            <button className="btn" onClick={() => setCollapsedTables(new Set())}>
              ➕ Expand All
            </button>
            <button className="btn" onClick={checkBalance}>
              ⚖️ Check Table Balance
            </button>
            <button className="btn btn-warning" onClick={proposeRedraw}>
              🔄 Redraw Tables (Chip-Balanced)
            </button>
            <button
              className={`btn ${lockMode ? 'btn-danger' : ''}`}
              onClick={() => setLockMode(!lockMode)}
            >
              {lockMode ? '🔒 Exit Lock Mode' : '🔒 Lock Seats'}
            </button>
          </div>
          
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span><strong>{tableConfigs.length}</strong> tables</span>
            <span><strong>{unseatedPlayers.length}</strong> unseated</span>
          </div>
        </div>
      </div>


      {/* Table Setup Section */}
      {showTableSetup && (
        <div className="card" style={{ background: '#f8f9fa' }}>
          <h3>Table Configuration</h3>
          
          {/* Add New Table */}
          <div style={{ marginBottom: '20px', padding: '15px', background: 'white', borderRadius: '5px' }}>
            <h4>Add New Table</h4>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Table Number:</label>
                <input 
                  type="number"
                  className="form-control"
                  value={newTableNumber}
                  onChange={(e) => setNewTableNumber(e.target.value)}
                  placeholder="1"
                  min="1"
                  max="99"
                  style={{ width: '100px' }}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Seat Count:</label>
                <select 
                  className="form-control"
                  value={newSeatCount}
                  onChange={(e) => setNewSeatCount(e.target.value)}
                  style={{ width: '100px' }}
                >
                  <option value="6">6</option>
                  <option value="8">8</option>
                  <option value="9">9</option>
                  <option value="10">10</option>
                </select>
              </div>
              <button className="btn btn-success" onClick={addTable}>
                Add Table
              </button>
            </div>
          </div>

          {/* Existing Tables */}
          <div>
            <h4>Configured Tables ({tableConfigs.length})</h4>
            {tableConfigs.length === 0 ? (
              <p>No tables configured. Add tables above to get started.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                {tableConfigs.map(config => {
                  const seatedCount = tables.find(t => t.tableNumber === config.table_number)?.seats.length || 0;
                  return (
                    <div key={config.id} style={{ 
                      padding: '15px', 
                      background: 'white', 
                      borderRadius: '5px',
                      border: '2px solid #ddd'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong style={{ fontSize: '18px' }}>Table {config.table_number}</strong>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {config.seat_count} seats | {seatedCount} occupied
                          </div>
                        </div>
                        <button 
                          className="btn btn-danger"
                          onClick={() => deleteTable(config.id, config.table_number)}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
x
      {redrawProposal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.7)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '10px', maxWidth: '750px', maxHeight: '85vh', overflowY: 'auto', color: '#333' }}>
            <h3 style={{ marginTop: 0 }}>
              Proposed Redraw — {redrawProposal.num_tables} tables, {redrawProposal.num_players} players
            </h3>
            <p style={{ color: '#666', fontSize: '14px' }}>
              Chip-balanced via snake draft. Review the balance below before confirming.
            </p>

            {redrawProposal.tables.map(table => (
              <div key={table.table_number} style={{ marginBottom: '15px', padding: '12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Table {table.table_number}</span>
                  <span>{table.player_count} players · {table.total_chips.toLocaleString()} chips</span>
                </div>
                <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                  {table.players.map(p => (
                    <span key={p.registration_id} style={{ display: 'inline-block', marginRight: '12px' }}>
                      Seat {p.seat_number}: {p.name} ({p.chips.toLocaleString()})
                    </span>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button className="btn btn-success" onClick={executeRedraw}>
                ✓ Confirm & Apply Redraw
              </button>
              <button className="btn" onClick={() => printRedrawSheets(redrawProposal, tournament?.event_name)}>
                🖨️ Print Sheets
              </button>
              <button className="btn" onClick={() => setRedrawProposal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unseated Players - Draggable */}
      {unseatedPlayers.length > 0 && (
        <div className="card" style={{ background: '#fff3cd' }}>
          <h3>⚠️ Unseated Players ({unseatedPlayers.length})</h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            💡 <strong>Drag and drop</strong> players onto empty seats below, or click <strong>Auto-Seat All</strong>
          </p>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {unseatedPlayers.map(player => (
             <div
                key={player.id}
                draggable
                onDragStart={(e) => handleDragStart(e, player, null)}
                onDragEnd={handleDragEnd}
                onClick={() => handlePlayerTap(player, null)}
                style={{
                  padding: '8px 12px',
                  background: selectedPlayer && selectedPlayer.id === player.id && !selectedFromSeat ? '#cce5ff' : 'white',
                  border: selectedPlayer && selectedPlayer.id === player.id && !selectedFromSeat ? '2px solid #007bff' : '2px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.2s'
                }}
              >
                <span style={{ marginRight: '5px' }}>🎯</span>
                {player.first_name} {player.last_name}
                {player.rebuy_count > 0 && (
                  <span style={{ marginLeft: '5px', color: '#ff9800' }}>
                    🔄{player.rebuy_count}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {balanceSuggestion && balanceSuggestion.suggestion !== 'none' && balanceSuggestion.suggestion !== 'balanced' && (
        <div style={{
          padding: '15px',
          margin: '15px 0',
          borderRadius: '8px',
          background: balanceSuggestion.suggestion === 'break_table' ? '#fff3cd' : '#cce5ff',
          border: `2px solid ${balanceSuggestion.suggestion === 'break_table' ? '#ffc107' : '#007bff'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontWeight: 'bold' }}>
            ⚖️ Balance Suggestion: {balanceSuggestion.message}
          </span>
          <button className="btn" onClick={() => setBalanceSuggestion(null)} style={{ fontSize: '11px', padding: '4px 8px' }}>
            Dismiss
          </button>
        </div>
      )}

      {/* Tables Display */}
      {tableConfigs.length === 0 ? (
        <div className="card">
          <p>No tables configured. Click "Table Setup" to add tables.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '20px' }}>
          {tableConfigs.map(config => {
            const table = tables.find(t => t.tableNumber === config.table_number);
            return (
              <TableCard
                key={config.id}
                config={config}
                table={table}
                onEliminate={startElimination}
                onRebuy={addRebuy}
                onAddon={addAddon}
                onUnseat={unseatPlayer}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                dragOverSeat={dragOverSeat}
                onPlayerTap={handlePlayerTap}
                onSeatTap={handleSeatTap}
                selectedPlayer={selectedPlayer}
                isCollapsed={collapsedTables.has(config.table_number)}
                onToggleCollapse={toggleTableCollapse}
                onBreakTable={breakTableAndReseat}
                lockMode={lockMode}
                isSeatLocked={isSeatLocked}
                onToggleLock={toggleSeatLock}
              />
            );
          })}
        </div>
      ) : (
        <div className="card">
          <h3>All Tables</h3>
          {tableConfigs.map(config => {
            const table = tables.find(t => t.tableNumber === config.table_number);
            return (
              <TableList 
                key={config.id}
                config={config}
                table={table}
                onEliminate={startElimination}
                onRebuy={addRebuy}
                onAddon={addAddon}
                onUnseat={unseatPlayer}
              />
            );
          })}
        </div>
      )}

      {/* Elimination Modal */}
      {showEliminationModal && eliminatingPlayer && (
        <EliminationModal
          player={eliminatingPlayer}
          position={position}
          setPosition={setPosition}
          prizeAmount={prizeAmount}
          setPrizeAmount={setPrizeAmount}
          eliminatedByPlayer={eliminatedByPlayer}
          setEliminatedByPlayer={setEliminatedByPlayer}
          allPlayers={tables.flatMap(t => t.seats).filter(s => s.player_status === 'active')}
          onConfirm={confirmElimination}
          onCancel={() => {
            setShowEliminationModal(false);
            setEliminatingPlayer(null);
          }}
        />
      )}
    </div>
  );
};

/**
 * Table Card Component (Grid View) with Full Drag-and-Drop
 */
/**
 * Table Card Component (Grid View) with Full Drag-and-Drop
 */

const TableCard = ({ config, table, onEliminate, onUnseat, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop, dragOverSeat, onPlayerTap, onSeatTap, selectedPlayer, isCollapsed, onToggleCollapse, onBreakTable, lockMode, isSeatLocked, onToggleLock }) => {
  const activePlayers = table?.seats.filter(s => s.player_status === 'active').length || 0;

  // Create array of all seats (filled and empty)
  const allSeats = [];
  for (let i = 1; i <= config.seat_count; i++) {
    const seat = table?.seats.find(s => s.seat_number === i);
    allSeats.push(seat || { seat_number: i, empty: true });
  }

  return (
    <div className="card">
      <h3
        onClick={() => onToggleCollapse(config.table_number)}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
      >
        <span>
          {isCollapsed ? '▶' : '▼'} Table {config.table_number}{' '}
          <span style={{ fontSize: '16px', color: '#666' }}>({activePlayers}/{config.seat_count})</span>
        </span>
        {activePlayers > 0 && (
          <button
            className="btn btn-danger"
            onClick={(e) => { e.stopPropagation(); onBreakTable(config.table_number); }}
            style={{ fontSize: '11px', padding: '4px 10px' }}
          >
            Break & Reseat
          </button>
        )}
      </h3>

      {!isCollapsed && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '10px',
          marginTop: '15px'
        }}>
          {allSeats.map(seat => {
            const seatKey = `${config.table_number}-${seat.seat_number}`;
            const isDragOver = dragOverSeat === seatKey;
            const locked = isSeatLocked(config.table_number, seat.seat_number);

            return (
              <div
                key={seat.seat_number}
                draggable={!seat.empty && seat.player_status === 'active'}
                onDragStart={!seat.empty && seat.player_status === 'active' ? (e) => {
                  const playerData = {
                    id: seat.registration_id,
                    first_name: seat.first_name,
                    last_name: seat.last_name,
                    player_id: seat.player_id
                  };
                  const fromSeatData = {
                    table_number: config.table_number,
                    seat_number: seat.seat_number,
                    seatAssignmentId: seat.id
                  };
                  onDragStart(e, playerData, fromSeatData);
                } : null}
                onDragEnd={onDragEnd}
                onDragOver={seat.empty || (seat.player_status === 'active') ? (e) => onDragOver(e, config.table_number, seat.seat_number) : null}
                onDragLeave={onDragLeave}
                onDrop={seat.empty && !locked ? (e) => onDrop(e, config.table_number, seat.seat_number, false) : null}
                onClick={() => {
                  if (lockMode) {
                    if (seat.empty) onToggleLock(config.table_number, seat.seat_number);
                    return;
                  }
                  if (locked) return;
                  if (seat.empty) {
                    onSeatTap(config.table_number, seat.seat_number, false);
                  } else if (seat.player_status === 'active') {
                    if (selectedPlayer && selectedPlayer.id !== seat.registration_id) {
                      onSeatTap(config.table_number, seat.seat_number, true);
                    } else {
                      onPlayerTap(
                        { id: seat.registration_id, first_name: seat.first_name, last_name: seat.last_name, player_id: seat.player_id },
                        { table_number: config.table_number, seat_number: seat.seat_number, seatAssignmentId: seat.id }
                      );
                    }
                  }
                }}
                style={{
                  padding: '12px',
                  background: locked ? '#d0d0d0' :
                             isDragOver ? '#d4edda' :
                             (selectedPlayer && !seat.empty && selectedPlayer.id === seat.registration_id) ? '#cce5ff' :
                             seat.empty ? '#f5f5f5' :
                             seat.player_status === 'eliminated' ? '#ffe6e6' : '#e8f5e8',
                  border: isDragOver ? '3px dashed #28a745' :
                         seat.empty ? '2px dashed #ccc' : '2px solid #ddd',
                  borderRadius: '8px',
                  textAlign: 'center',
                  opacity: seat.player_status === 'eliminated' ? 0.5 : 1,
                  minHeight: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s',
                  cursor: lockMode && seat.empty ? 'pointer' : seat.empty ? 'copy' : seat.player_status === 'active' ? 'grab' : 'default'
                }}>
                <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '5px', color: '#666' }}>
                  Seat {seat.seat_number}
                </div>

                {seat.empty ? (
                  locked ? (
                    <div style={{ fontSize: '13px', color: '#555', fontWeight: 'bold' }}>🔒 LOCKED</div>
                  ) : (
                    <div style={{
                      fontSize: '12px',
                      color: isDragOver ? '#28a745' : '#999',
                      fontStyle: 'italic',
                      fontWeight: isDragOver ? 'bold' : 'normal'
                    }}>
                      {isDragOver ? '↓ Drop Here' : (lockMode ? '🔓 Tap to lock' : 'Empty')}
                    </div>
                  )
                ) : (
                  <>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      {seat.player_status === 'active' && <span style={{ marginRight: '5px' }}>↔️</span>}
                      {seat.first_name} {seat.last_name}
                    </div>
                    {seat.current_chips && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        💰 {seat.current_chips.toLocaleString()}
                      </div>
                    )}
                    {seat.rebuy_count > 0 && (
                      <div style={{ fontSize: '11px', color: '#FF9800' }}>
                        🔄 {seat.rebuy_count} rebuy{seat.rebuy_count > 1 ? 's' : ''}
                      </div>
                    )}
                    {seat.player_status === 'active' && (
                      <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexDirection: 'column' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onEliminate(seat); }}
                          className="btn btn-danger"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                        >
                          Eliminate
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onUnseat(seat.id, `${seat.first_name} ${seat.last_name}`, config.table_number, seat.seat_number); }}
                          className="btn"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                        >
                          Unseat
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Table List Component (List View)
 */
const TableList = ({ config, table, onEliminate, onUnseat }) => {
  if (!table || table.seats.length === 0) {
    return (
      <div style={{ marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', padding: '15px' }}>
        <h4>Table {config.table_number}</h4>
        <p style={{ color: '#666' }}>No players seated ({config.seat_count} seats available)</p>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', padding: '15px' }}>
      <h4>Table {config.table_number}</h4>
      <table className="table">
        <thead>
          <tr>
            <th>Seat</th>
            <th>Player</th>
            <th>Chips</th>
            <th>Rebuys</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {table.seats.map(seat => (
            <tr key={seat.id} style={{ 
              background: seat.player_status === 'eliminated' ? '#ffe6e6' : 'white'
            }}>
              <td><strong>{seat.seat_number}</strong></td>
              <td>
                {seat.player_status === 'active' && <span style={{ marginRight: '5px' }}>↔️</span>}
                <strong>{seat.first_name} {seat.last_name}</strong>
              </td>
              <td>{seat.current_chips?.toLocaleString() || 'N/A'}</td>
              <td>
                {seat.rebuy_count > 0 ? (
                  <span style={{ color: '#ff9800', fontWeight: 'bold' }}>
                    🔄 {seat.rebuy_count}
                  </span>
                ) : '-'}
              </td>
              <td>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '3px',
                  fontSize: '12px',
                  background: seat.player_status === 'eliminated' ? '#f8d7da' : '#d4edda',
                  color: seat.player_status === 'eliminated' ? '#721c24' : '#155724'
                }}>
                  {seat.player_status?.toUpperCase()}
                </span>
              </td>
              <td>
                {seat.player_status === 'active' && (
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button 
                      onClick={() => onEliminate(seat)}
                      className="btn btn-danger"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Eliminate
                    </button>
                    <button 
                      onClick={() => onUnseat(seat.id, `${seat.first_name} ${seat.last_name}`, config.table_number, seat.seat_number)}
                      className="btn"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Unseat
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Elimination Modal Component
 */
const EliminationModal = ({ 
  player, 
  position, 
  setPosition, 
  prizeAmount, 
  setPrizeAmount, 
  eliminatedByPlayer, 
  setEliminatedByPlayer, 
  allPlayers,
  onConfirm, 
  onCancel 
}) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        padding: '30px',
        borderRadius: '8px',
        maxWidth: '500px',
        width: '90%'
      }}>
        <h2>Eliminate Player</h2>
        <p style={{ fontSize: '18px', marginBottom: '20px' }}>
          <strong>{player.first_name} {player.last_name}</strong> from Table {player.table_number}, Seat {player.seat_number}
        </p>

        <div className="form-group">
          <label>Finishing Position:</label>
          <input 
            type="number"
            className="form-control"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            placeholder="e.g., 5"
          />
        </div>

        <div className="form-group">
          <label>Prize Amount ($):</label>
          <input 
            type="number"
            step="0.01"
            className="form-control"
            value={prizeAmount}
            onChange={(e) => setPrizeAmount(e.target.value)}
            placeholder="0.00"
          />
        </div>

        <div className="form-group">
          <label>Eliminated By (optional):</label>
          <select 
            className="form-control"
            value={eliminatedByPlayer}
            onChange={(e) => setEliminatedByPlayer(e.target.value)}
          >
            <option value="">-- Select Player --</option>
            {allPlayers.filter(p => p.registration_id !== player.registration_id).map(p => (
              <option key={p.registration_id} value={p.player_id}>
                {p.first_name} {p.last_name} (Table {p.table_number})
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button className="btn btn-danger" onClick={onConfirm}>
            ✓ Confirm Elimination
          </button>
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default TableManager;
